import { type FormEvent, useState } from 'react';
import { KeyRound, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/lib/api';
import { Button } from '@/ui/Button';
import { Field } from '@/ui/Field';
import { Logo } from '@/ui/Logo';
import { PasswordInput } from '@/ui/PasswordInput';
import { useChangePassword, useLogout } from './session';

const MIN_LENGTH = 10;

/**
 * The change itself, shared by the two ways into it: the screen that stands in
 * for the application while a handed-out password is pending, and the dialog on
 * your own profile for changing it whenever you like.
 */
export function ChangePasswordForm({ onDone }: { onDone?: () => void }) {
  const { t } = useTranslation();
  const change = useChangePassword();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');

  const error = change.error instanceof ApiError ? change.error : null;
  const fields = error?.fieldMap ?? {};

  const tooShort = newPassword.length > 0 && newPassword.length < MIN_LENGTH;
  const mismatch = confirmation.length > 0 && newPassword !== confirmation;
  const ready = currentPassword && newPassword.length >= MIN_LENGTH && !mismatch;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ready) change.mutate({ currentPassword, newPassword }, { onSuccess: onDone });
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <Field label={t('auth.currentPassword')} error={fields.currentPassword} required>
        <PasswordInput value={currentPassword} autoComplete="current-password" required
                       error={Boolean(fields.currentPassword)}
                       onChange={(e) => setCurrentPassword(e.target.value)} />
      </Field>

      <Field
        label={t('auth.newPassword')}
        error={fields.newPassword ?? (tooShort ? t('auth.passwordTooShort', { count: MIN_LENGTH }) : undefined)}
        required
      >
        <PasswordInput value={newPassword} autoComplete="new-password" required
                       error={Boolean(fields.newPassword) || tooShort}
                       onChange={(e) => setNewPassword(e.target.value)} />
      </Field>

      <Field
        label={t('auth.confirmPassword')}
        error={mismatch ? t('auth.passwordMismatch') : undefined}
        required
      >
        <PasswordInput value={confirmation} autoComplete="new-password" required
                       error={mismatch}
                       onChange={(e) => setConfirmation(e.target.value)} />
      </Field>

      {error && !error.fields?.length && (
        <p role="alert" className="rounded-[var(--radius-control)] bg-danger-soft px-3 py-2 text-sm text-danger">
          {error.message}
        </p>
      )}

      <Button type="submit" loading={change.isPending} disabled={!ready}
              icon={<KeyRound className="size-4" aria-hidden />}>
        {t('auth.savePassword')}
      </Button>
    </form>
  );
}

/**
 * Shown in place of the application - not as a route - while the password is
 * still the one somebody else handed out. A route could be navigated around;
 * this cannot, and it mirrors the server, which refuses every other endpoint
 * until the change is made.
 */
export function ChangePasswordScreen() {
  const { t } = useTranslation();
  const logout = useLogout();

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-4">
      <div className="w-full max-w-sm">
        <Logo className="mb-8" />

        <h1 className="text-xl font-semibold text-ink">{t('auth.choosePassword')}</h1>
        <p className="mt-1 mb-6 text-sm text-ink-muted">{t('auth.choosePasswordWhy')}</p>

        <ChangePasswordForm />

        {/* A way out for someone who signed in as the wrong person. */}
        <div className="mt-6 flex justify-center">
          <Button variant="ghost" size="sm" loading={logout.isPending}
                  icon={<LogOut className="size-3.5 rtl:rotate-180" aria-hidden />}
                  onClick={() => logout.mutate()}>
            {t('nav.signOut')}
          </Button>
        </div>
      </div>
    </div>
  );
}
